'use client'
import { useSearchParams, usePathname } from 'next/navigation'
import './styles.scss'
import { ArrowLeft, ArrowRight } from 'lucide-react'

interface PaginationProps {
  currentPage: number
  totalPages: number
  pageSize?: number
}

export default function Pagination({
  currentPage,
  totalPages,
}: PaginationProps) {
  const searchParams = useSearchParams()
  const pathname = usePathname()
  const params = new URLSearchParams(searchParams)

  const routerPush = (page: number) => {
    let target: string
    const segments = pathname.split('/').filter(Boolean)

    if (segments[0] === 'products') {
      // Preserve existing filter searchParams when paginating
      const qs = params.toString()
      target = `/products/${page}${qs ? `?${qs}` : ''}`
    } else if (segments[0] === 'sector' && segments.length >= 2) {
      const slug = segments[1]
      target = `/sector/${slug}/${page}`
    } else {
      params.set('page', String(page))
      target = `${pathname}?${params.toString()}`
    }

    window.location.href = target
  }

  const getVisiblePages = () => {
    const visiblePages: (number | string)[] = []
    const maxVisible = 10
    const ellipsis = '...'

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        visiblePages.push(i)
      }
    } else {
      const left = Math.max(1, currentPage - Math.floor(maxVisible / 2))
      const right = Math.min(
        totalPages,
        currentPage + Math.floor(maxVisible / 2),
      )

      if (left > 1) {
        visiblePages.push(1)
        if (left > 2) visiblePages.push(ellipsis)
      }

      for (let i = left; i <= right; i++) {
        visiblePages.push(i)
      }

      if (right < totalPages) {
        if (right < totalPages - 1) visiblePages.push(ellipsis)
        visiblePages.push(totalPages)
      }
    }

    return visiblePages
  }

  const visiblePages = getVisiblePages()

  return (
    <div className="pagination">
      <button
        onClick={() => routerPush(currentPage - 1)}
        disabled={currentPage === 1}
        aria-label="Página anterior"
      >
        <ArrowLeft size={24} />
      </button>
      {visiblePages.map((page, idx) =>
        page === '...' ? (
          <span key={idx} className="ellipsis">
            {page}
          </span>
        ) : (
          <button
            key={idx}
            onClick={() => routerPush(Number(page))}
            className={currentPage === page ? 'active' : ''}
          >
            {page}
          </button>
        ),
      )}
      <button
        onClick={() => routerPush(currentPage + 1)}
        disabled={currentPage === totalPages}
        aria-label="Próxima página"
      >
        <ArrowRight size={24} />
      </button>
    </div>
  )
}
